import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowUpRight,
  DollarSign,
  Download,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import { statsApi } from "@/lib/stats-api";
import { downloadFromUrl } from "@/lib/download";
import { monogramStyle, monogramText } from "@/lib/refclick-utils";

/**
 * Earnings section on the /stats page.
 *
 * Layout:
 *   [ All-time total ]  [ This month ]
 *   [ 6-month bar chart · animated ]
 *   [ Per-link ROI table: name · $ total · $/click (30d) · entries ]
 *
 * Data is fetched lazily on mount. Empty state ("No earnings logged
 * yet.") replaces the whole block if `total_all_time === 0`.
 */
export default function EarningsStatsSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    statsApi
      .earningsSummary()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        /* Non-critical — leave data null, we render the friendly empty
           state which fits the "no data yet" experience. */
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleExport = async () => {
    setDownloading(true);
    try {
      const url = statsApi.exportEarningsCsvUrl();
      const stamp = new Date().toISOString().slice(0, 10);
      await downloadFromUrl(url, `refclick-earnings-${stamp}.csv`);
      toast.success("Earnings CSV downloaded");
    } catch {
      toast.error("Export failed — try again?");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <section
        className="mt-6 rounded-xl border border-white/5 bg-white/[0.02] px-3 py-6 text-center font-mono-safe text-[11px] text-text-muted sm:px-5"
        data-testid="earnings-stats-section"
      >
        Loading earnings…
      </section>
    );
  }

  const hasData = data && data.total_all_time > 0;
  // Trust boundary — coerce server array fields to actual arrays. A
  // legacy shape (dict/string/null) must never crash the render with
  // "t.map is not a function".
  const perLink = Array.isArray(data?.per_link) ? data.per_link : [];
  const months = Array.isArray(data?.monthly_series) ? data.monthly_series : [];
  const monthMax = Math.max(
    1,
    ...months.map((m) => Number(m?.total_usd) || 0),
  );

  return (
    <section
      className="mt-6 rounded-xl border border-white/5 bg-white/[0.02] px-3 py-4 sm:px-5"
      data-testid="earnings-stats-section"
      data-has-data={hasData ? "true" : "false"}
    >
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-[11px] font-bold uppercase tracking-[0.22em] text-text-muted">
          Earnings · USD
        </h3>
        <ExportButton
          disabled={downloading || !hasData}
          downloading={downloading}
          onClick={handleExport}
        />
      </div>

      {/* Totals tiles — always render so the layout doesn't collapse when
          there's no data yet. They just read $0.00 until entries land. */}
      <div className="mb-4 grid grid-cols-2 gap-2 sm:gap-3">
        <TotalTile
          icon={<DollarSign className="h-4 w-4" />}
          label="All-time total"
          value={data?.total_all_time || 0}
          testid="earnings-total-alltime"
          tone={hasData ? "accent" : "muted"}
        />
        <TotalTile
          icon={<ArrowUpRight className="h-4 w-4" />}
          label="This month"
          value={data?.total_this_month || 0}
          testid="earnings-total-month"
          tone="muted"
        />
      </div>

      {/* 6-month chart — empty months render as faint slabs; the shell
          stays even without data so the user sees the frame they'll fill. */}
      <div
        className="mb-4 rounded-lg border border-white/5 bg-white/[0.015] px-3 py-3"
        data-testid="earnings-monthly-series"
      >
        <div className="mb-2 flex items-center justify-between">
          <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-text-muted">
            Last 6 months
          </span>
          <span className="font-mono-num text-[11px] text-text-muted">
            {months.length} months
          </span>
        </div>
        <div
          className="flex h-24 items-end gap-1.5"
          data-testid="earnings-monthly-bars"
        >
          {months.map((m, i) => {
            const pct = hasData
              ? Math.max(
                  m.total_usd > 0 ? 4 : 2,
                  Math.round(((m.total_usd || 0) / monthMax) * 100),
                )
              : 2;
            const isCurrent = i === months.length - 1;
            return (
              <div
                key={m.month}
                className="group relative flex h-full flex-1 flex-col items-center justify-end gap-1"
                data-testid="earnings-month-col"
                data-month={m.month}
                data-total={m.total_usd}
                title={`${m.month || ""}: $${Number(m.total_usd || 0).toFixed(2)}`}
              >
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: `${pct}%` }}
                  transition={{
                    duration: 0.65,
                    delay: i * 0.06,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                  className={
                    "w-full rounded-t-sm " +
                    (hasData
                      ? isCurrent
                        ? "bg-accent"
                        : m.total_usd > 0
                          ? "bg-accent/50"
                          : "bg-white/[0.05]"
                      : "bg-white/[0.05]")
                  }
                  style={{
                    boxShadow:
                      hasData && m.total_usd > 0
                        ? "0 0 8px rgb(var(--accent) / 0.45)"
                        : "none",
                    minHeight: "3px",
                  }}
                />
                <span className="font-mono-safe text-[9px] leading-none text-text-muted/70">
                  {(m.month || "").slice(5)}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {!hasData ? (
        <p
          data-testid="earnings-stats-empty"
          className="rounded-lg border border-dashed border-white/10 py-4 text-center font-mono-safe text-[11px] text-text-muted"
        >
          No earnings logged yet — add your first from a link&apos;s detail
          panel.
        </p>
      ) : (
        /* Per-link ROI table */
        <div className="rounded-lg border border-white/5 bg-white/[0.015]">
          <div className="grid grid-cols-[1fr_auto_auto_auto] gap-3 border-b border-white/5 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-text-muted">
            <span>Link</span>
            <span className="text-right">Total</span>
            <span className="hidden text-right sm:inline">$/click 30d</span>
            <span className="text-right">Entries</span>
          </div>
          <ul
            className="flex flex-col"
            data-testid="earnings-per-link-list"
          >
            <AnimatePresence initial={false}>
              {perLink.map((row) => (
                <motion.li
                  key={row.link_id}
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                  className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-3 border-b border-white/5 px-3 py-2 last:border-b-0"
                  data-testid="earnings-per-link-row"
                  data-link-id={row.link_id}
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <div
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md font-display text-[10px] font-bold text-white/95"
                      style={monogramStyle(row.name)}
                      aria-hidden
                    >
                      {monogramText(row.name)}
                    </div>
                    <span
                      className="truncate text-sm text-text-main"
                      title={row.name}
                    >
                      {row.name}
                    </span>
                  </div>
                  <span className="text-right font-mono-num text-sm text-accent">
                    ${Number(row.total_usd || 0).toFixed(2)}
                  </span>
                  <span className="hidden text-right font-mono-num text-[11px] text-text-muted sm:inline">
                    {row.clicks_30d > 0
                      ? `$${Number(row.usd_per_click_30d || 0).toFixed(2)}`
                      : "—"}
                  </span>
                  <span className="text-right font-mono-num text-[11px] text-text-muted">
                    {row.entries_count}
                  </span>
                </motion.li>
              ))}
            </AnimatePresence>
          </ul>
        </div>
      )}
    </section>
  );
}

/**
 * Export CSV button.
 * When disabled (no data), wrap in a Tooltip so the user learns *why*
 * it's greyed out. Enabled state has no tooltip — the icon speaks for
 * itself.
 */
function ExportButton({ disabled, downloading, onClick }) {
  const button = (
    <Button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid="earnings-export-btn"
      className="h-8 gap-1.5 rounded-lg border border-white/10 bg-white/[0.03] px-2.5 text-xs text-text-muted hover:border-accent/40 hover:text-accent disabled:opacity-40 disabled:hover:border-white/10 disabled:hover:text-text-muted"
    >
      {downloading ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <Download className="h-3 w-3" />
      )}
      CSV
    </Button>
  );
  if (!disabled || downloading) return button;
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        {/* asChild + span so the disabled button still triggers the tooltip
            in browsers that swallow pointer events on disabled controls. */}
        <TooltipTrigger asChild>
          <span
            data-testid="earnings-export-btn-tooltip-trigger"
            className="inline-flex"
          >
            {button}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          sideOffset={6}
          data-testid="earnings-export-tooltip"
          className="border-white/10 bg-black/80 font-mono-safe text-[11px] text-text-main"
        >
          Add at least one earning to enable export.
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function TotalTile({ icon, label, value, testid, tone }) {
  const accented = tone === "accent";
  return (
    <div
      className={
        "rounded-xl border px-3 py-3 sm:px-4 sm:py-4 " +
        (accented
          ? "border-accent/40 bg-accent/10"
          : "border-white/5 bg-white/[0.02]")
      }
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-text-muted">
        <span className={accented ? "text-accent" : "text-text-muted"}>
          {icon}
        </span>
        <span className="truncate">{label}</span>
      </div>
      <div className="mt-2 font-display text-2xl font-bold leading-none sm:text-3xl">
        <span className={accented ? "text-accent" : "text-text-main"}>
          ${Number(value || 0).toFixed(2)}
        </span>
      </div>
    </div>
  );
}
